import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import StructuredData, { courseSchema, breadcrumbSchema } from "../components/StructuredData";

const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

export default function ThirtyDay() {
  const loc = useLocation();
  const isSuccess = loc.pathname.endsWith("/success");
  const sessionId = new URLSearchParams(loc.search).get("session_id");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let t;
    const poll = async (n = 0) => {
      try {
        const { data } = await api.get(`/drip-30/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setConfirmed(data);
          return;
        }
      } catch {}
      if (n < 8) t = setTimeout(() => poll(n + 1), 2500);
    };
    poll();
    return () => t && clearTimeout(t);
  }, [isSuccess, sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Email is required"); return; }
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/drip-30/checkout", { email: email.trim(), name, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="thirty-day-page" className="relative min-h-[80vh]"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.92), rgba(249,247,243,0.98)), url(${POWDER})`,
        backgroundSize: "cover",
      }}>
      <SEO
        title="30 Brushes in 30 Days — Daily Makeup Brush Course"
        description="One brush, one lesson, delivered to your inbox every morning for a month. $19.99 for a 30-day emailed makeup brush masterclass from BrushBasics."
        path="/thirty-day"
      />
      <StructuredData
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "30 Brushes in 30 Days", path: "/thirty-day" },
          ]),
          courseSchema,
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="overline mb-4">Volume 03 · Daily Course</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-stone-900 tracking-tight">
            30 Brushes.<br />
            <em className="italic text-stone-700">30 Days.</em>
          </h1>
          <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-stone-600 font-light">
            One brush per day, delivered to your inbox at 8am for a month. Each lesson covers what the brush does, how to use it, technique tips, and care. Build the habit. Master the kit.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            <div className="border-t border-[#E5E0D8] pt-4"><div className="font-serif-display text-3xl text-stone-900">30</div><div className="overline mt-1">Lessons</div></div>
            <div className="border-t border-[#E5E0D8] pt-4"><div className="font-serif-display text-3xl text-stone-900">1/day</div><div className="overline mt-1">Pace</div></div>
            <div className="border-t border-[#E5E0D8] pt-4"><div className="font-serif-display text-3xl text-stone-900">$19.99</div><div className="overline mt-1">One-time</div></div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="bg-white border border-[#E5E0D8] p-8 lg:p-10">
            {confirmed ? (
              <div data-testid="thirty-day-success">
                <div className="overline mb-3">Subscribed</div>
                <h2 className="font-serif-display text-3xl text-stone-900 font-light leading-tight">Day 1 is on its way.</h2>
                <p className="mt-4 text-sm text-stone-600 leading-relaxed">
                  We sent the first lesson to <span className="text-stone-900 font-medium">{confirmed.email}</span> just now. Check your inbox (and spam folder, just in case). The next 29 will land at the same time each day.
                </p>
                <Link to="/catalog" className="inline-block mt-8 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors">
                  Explore the Catalog
                </Link>
              </div>
            ) : isSuccess ? (
              <div data-testid="thirty-day-confirming">
                <div className="overline mb-3">Confirming</div>
                <h2 className="font-serif-display text-3xl text-stone-900 font-light leading-tight">Confirming your payment…</h2>
                <p className="mt-4 text-sm text-stone-600">Just a moment.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="thirty-day-form">
                <div className="overline mb-3">Get started</div>
                <h2 className="font-serif-display text-3xl text-stone-900 font-light leading-tight">Enter your email.</h2>
                <p className="mt-2 text-sm text-stone-500">We'll start the course immediately after checkout.</p>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="overline block mb-2" htmlFor="td-name">Name (optional)</label>
                    <input id="td-name" data-testid="td-name" type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3" />
                  </div>
                  <div>
                    <label className="overline block mb-2" htmlFor="td-email">Email</label>
                    <input id="td-email" data-testid="td-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3" />
                  </div>
                </div>
                {error && <p data-testid="td-error" className="mt-4 text-sm text-red-700">{error}</p>}
                <button data-testid="td-submit" type="submit" disabled={loading} className="mt-6 w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors">
                  {loading ? "Redirecting…" : "Start the Course — $19.99"}
                </button>
                <p className="mt-4 text-xs text-stone-500 leading-relaxed">
                  One-time payment. 30 daily emails. No subscription. Unsubscribe from any email.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
